import { UpdateContactInput } from 'app/generated/graphql'
import { ContactJson } from 'app/types/entities'
import { systemPipeline } from './systemPipelines'
import { debug } from 'lib/logging'

export type BoardColumn = {
  key: string
  label: string
  color?: string
  /**
   * Moving a card here takes it out of the queue's own search, so it leaves the
   * board on the next read. Not a place a card rests.
   */
  exit?: boolean
}

/**
 * Where a board's columns come from, and where a move is written.
 *
 * The two implementations differ in substrate, not in behaviour: Active Leads
 * moves a contact's `leadStatus`, everything else moves a pipeline membership.
 * Neither ever writes to `localStorage`. A board is a shared record, and a
 * local fallback is the problem this whole design removes.
 */
export type BoardColumnSource = {
  columns: () => readonly BoardColumn[]
  /** Which column this contact sits in, or undefined if none of them. */
  columnOf: (contact: ContactJson) => string | undefined
  /**
   * The contact as it would look right after the move, computed but not sent.
   * A board patches its optimistic copy with this before `move`'s write
   * resolves, so the card jumps immediately and `columnOf` agrees with where
   * it now sits without waiting on the round trip.
   */
  applyMove: (contact: ContactJson, columnKey: string) => ContactJson
  move: (contact: ContactJson, columnKey: string) => Promise<void>
}

/**
 * Persists a partial contact. Injected so the sources stay testable.
 *
 * Typed to the generated `UpdateContactInput` rather than a bag of unknowns:
 * a typo like `leadStatuss` should fail to compile, not fail silently on the
 * next mutation.
 */
export type WriteContact = (id: string, input: UpdateContactInput) => Promise<void>

/**
 * Closed Won is deliberately absent. It is system-set when a lead converts, and
 * a column for it would let somebody claim a conversion by dragging a card.
 */
// Exported so `systemPipelines.ts`'s `system:leads` stage list, and the spec
// that pins it, can compare against these keys instead of a second hardcoded
// copy. Not imported there directly: `systemPipelines.ts` is imported back
// into this file (`pipelineSource` below reads `systemPipeline`), so a
// runtime import the other way would make the two files circular.
export const LEAD_COLUMNS: BoardColumn[] = [
  { key: `Needs Action`, label: `Needs action`, color: `pipelines-salmon` },
  { key: `In Progress`, label: `In progress`, color: `pipelines-blue` },
  // The Active Leads queue searches for Needs Action and In Progress only, so
  // these two take the card off the board.
  { key: `Scheduled`, label: `Scheduled`, color: `pipelines-green`, exit: true },
  { key: `Closed Lost`, label: `Closed lost`, color: `pipelines-silver`, exit: true },
]

/**
 * Whether a move into this column has to collect a reason before anything is
 * written. Closed Lost is the only one: it is the status a lost-reporting
 * query depends on, and a drag must not become a cheaper way to record a
 * loss than the focus card's own reason modal is.
 *
 * Gated on `boardSource` rather than the key alone: Closed Lost only exists
 * as a column on the lead-status board (see `LEAD_COLUMNS`), and a pipeline
 * board's own exit stages (Scheduled, Lost, Completed, ...)
 * are a different substrate that happens never to reuse this literal key.
 * Checking the source makes that a guarantee instead of an assumption.
 */
export function needsClosedLostReason(boardSource: `lead-status` | `pipeline`, columnKey: string): boolean {
  return boardSource === `lead-status` && columnKey === `Closed Lost`
}

export function leadStatusSource(write: WriteContact): BoardColumnSource {
  return {
    columns: () => LEAD_COLUMNS,
    columnOf: (contact) => LEAD_COLUMNS.find((column) => column.key === contact.leadStatus)?.key,
    applyMove: (contact, columnKey) => ({ ...contact, leadStatus: columnKey }) as ContactJson,
    move: (contact, columnKey) => write(contact.id!, { leadStatus: columnKey }),
  }
}

/**
 * Moves a pipeline membership rather than a scalar field. Backs any board
 * whose columns are a system pipeline's stages (Hygiene, Treatment).
 *
 * `exit` comes from the stage itself (`systemPipelines.ts`), not from a name
 * matched here, because whether a stage leaves the board is a fact about that
 * pipeline, not about the word used for the stage: `Scheduled` is an exit for
 * Hygiene but would not have to be for some other pipeline that reused the name.
 *
 * Callers should memoize this by `pipelineId`: it builds a fresh `columns`
 * array on every call, which would defeat downstream memoization if a board
 * constructed it inside a render body.
 */
/**
 * A contact's memberships, ready to be written back.
 *
 * The coercion lives here and nowhere else. `stage` is `Maybe<String>` coming
 * out and required going in, and the tempting fix (drop the stageless rows)
 * would typecheck while silently removing the contact from another pipeline as
 * a side effect of a move on this one.
 */
export function membershipsOf(contact: ContactJson): { id: string; stage: string }[] {
  return (contact.pipelines ?? []).map(({ id, stage }) => ({ id, stage: stage ?? `` }))
}

export function pipelineSource(pipelineId: string, write: WriteContact): BoardColumnSource {
  const pipeline = systemPipeline(pipelineId)
  if (!pipeline) debug(`action-center`, `pipelineSource: no system pipeline for id`, pipelineId)
  const columns: BoardColumn[] = [...(pipeline?.stages ?? [])]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((stage) => ({
      key: stage.name,
      label: stage.name,
      color: stage.color,
      exit: stage.exit,
    }))

  // The whole array goes back, exactly as `setPipelineStage` does it, so a
  // move and a first enrolment are the same write and the contact's other
  // memberships survive it.
  //
  // The coercion below is not defensive noise. A membership's `stage` is
  // `Maybe<String>` on the way out but required on the way in, so the two
  // types genuinely disagree. Dropping a stageless membership would
  // typecheck and would silently remove that contact from another pipeline
  // as a side effect of a move on this one, which is the worst available
  // outcome. Preserving the row is the safe reading.
  const nextMemberships = (contact: ContactJson, columnKey: string) => {
    const existing = membershipsOf(contact)
    const index = existing.findIndex((m) => m.id === pipelineId)
    const next = [...existing]
    if (index >= 0) next[index] = { id: pipelineId, stage: columnKey }
    else next.push({ id: pipelineId, stage: columnKey })
    return next
  }

  return {
    columns: () => columns,
    columnOf: (contact) => (contact.pipelines ?? []).find((m) => m.id === pipelineId)?.stage ?? undefined,
    applyMove: (contact, columnKey) => ({ ...contact, pipelines: nextMemberships(contact, columnKey) }) as ContactJson,
    move: (contact, columnKey) => write(contact.id!, { pipelines: nextMemberships(contact, columnKey) }),
  }
}
